import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Tag } from 'lucide-react';
import { tagsApi } from '../../../lib/api.js';
import { Button } from '../../../components/ui/Button.js';
import { Input } from '../../../components/ui/Input.js';
import { SkeletonCard } from '../../../components/ui/Skeleton.js';
import { useToast } from '../../../components/ui/Toast.js';

export const Route = createFileRoute('/_auth/admin/tags')({
  component: TagsPage,
});

function TagsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newTag, setNewTag] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['tags'],
    queryFn: () => tagsApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => tagsApi.create(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      setNewTag('');
      toast('Tag created', 'success');
    },
    onError: (err: Error) => toast(err.message, 'error'),
  });

  const tags = data?.items ?? [];

  return (
    <div className="max-w-lg mx-auto p-4">
      <h1 className="text-xl font-bold text-primary-text mb-4">Tags</h1>

      {/* Add tag form */}
      <div className="bg-white border border-border rounded-xl p-4 mb-4 flex gap-2">
        <Input
          placeholder="New tag name…"
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && newTag.trim()) {
              createMutation.mutate(newTag.trim());
            }
          }}
          aria-label="New tag name"
        />
        <Button
          size="md"
          onClick={() => newTag.trim() && createMutation.mutate(newTag.trim())}
          loading={createMutation.isPending}
          disabled={!newTag.trim()}
          className="flex-shrink-0"
        >
          <Plus className="w-4 h-4" />
          Add
        </Button>
      </div>

      {/* Tags list */}
      <div className="bg-white border border-border rounded-xl p-4">
        {isLoading ? (
          <SkeletonCard />
        ) : tags.length === 0 ? (
          <div className="text-center py-8">
            <Tag className="w-10 h-10 text-secondary mx-auto mb-2" />
            <p className="text-secondary text-sm">No tags yet</p>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span
                key={tag.id}
                className="bg-primary/10 text-primary px-3 py-1.5 rounded-full text-sm font-medium"
              >
                {tag.name}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
